import {
    copyFilesToFolders,
    deleteFolder,
    fetchDocumentsFolders,
    generateFolderStructure,
    moveFilesToFolders,
    saveDocumentsFolder,
} from '@/api/documents-folders.api';
import type {
    ICopyFilesAndFoldersParams,
    IDocumentFolder,
    IFindDocumentFolders,
    IGenerateFolderStructureParams,
    IMoveFilesAndFoldersParams,
} from '@condo/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { pick } from 'lodash-es';
import { type MaybeRef, computed, unref } from 'vue';

export const useDocumentsFolders = (paramsOrRef: MaybeRef<IFindDocumentFolders['query']>, enabled: MaybeRef<boolean> = true) => {
    const queryKey = ['document-folders', paramsOrRef];

    return useQuery(queryKey, () => fetchDocumentsFolders(unref(paramsOrRef)), {
        initialData: [],
        enabled,
    });
};

export const useDocumentsFoldersMutations = () => {
    const queryClient = useQueryClient();

    const { mutateAsync: createFolderFunc, isLoading: createFolderFuncLoading } = useMutation({
        mutationFn: async (params: Partial<IDocumentFolder>) => {
            await saveDocumentsFolder(pick(params, ['folderId', 'estateId', 'mfhId', 'parentFolderId', 'folderName', 'description', 'tags']));
            await queryClient.invalidateQueries({ queryKey: ['document-folders'] });
        },
    });

    const { mutateAsync: moveFilesAndFoldersFunc, isLoading: moveFilesAndFolderLoading } = useMutation({
        mutationFn: async (params: IMoveFilesAndFoldersParams) => {
            await moveFilesToFolders(params);
            await queryClient.invalidateQueries({ queryKey: ['documents'] });
            await queryClient.invalidateQueries({ queryKey: ['document-folders'] });
        },
    });

    const { mutateAsync: copyFilesAndFoldersFunc, isLoading: copyFilesAndFolderLoading } = useMutation({
        mutationFn: async (params: ICopyFilesAndFoldersParams) => {
            const result = await copyFilesToFolders(params);
            await queryClient.invalidateQueries({ queryKey: ['documents'] });
            await queryClient.invalidateQueries({ queryKey: ['document-folders'] });
            return result;
        },
    });

    const { mutateAsync: deleteDocumentFolderFunc, isLoading: deleteDocumentFolderLoading } = useMutation({
        mutationFn: async (folderId: number) => {
            await deleteFolder(folderId);
            await queryClient.invalidateQueries({ queryKey: ['document-folders'] });
        },
    });

    const { mutateAsync: deleteDocumentFoldersFunc, isLoading: deleteDocumentFoldersLoading } = useMutation({
        mutationFn: async (folderIds: number[]) => {
            await Promise.all(folderIds.map(id => deleteFolder(id)));
            await Promise.all([queryClient.invalidateQueries({ queryKey: ['documents'] }), queryClient.invalidateQueries({ queryKey: ['document-folders'] })]);
        },
    });

    const { mutateAsync: generateFolderStructureFunc, isLoading: generateFolderStructureLoading } = useMutation({
        mutationFn: async (params: IGenerateFolderStructureParams) => {
            const result = await generateFolderStructure(params);
            await queryClient.invalidateQueries({ queryKey: ['document-folders'] });
            return result.data as Record<string, number>;
        },
    });

    const folderMutationLoading = computed(() => {
        return (
            createFolderFuncLoading.value ||
            moveFilesAndFolderLoading.value ||
            copyFilesAndFolderLoading.value ||
            deleteDocumentFolderLoading.value ||
            generateFolderStructureLoading.value
        );
    });

    return {
        createFolder: createFolderFunc,
        moveFilesAndFolders: moveFilesAndFoldersFunc,
        copyFilesAndFolders: copyFilesAndFoldersFunc,
        deleteDocumentFolder: deleteDocumentFolderFunc,
        deleteDocumentFolders: deleteDocumentFoldersFunc,
        generateFolderStructure: generateFolderStructureFunc,
        folderMutationLoading,
    };
};
